import type { CreatedTask } from "./task-schedule";
import { supportedReasoningEffort } from "./reasoning";
import { localeFor } from "./languages";
import { ui } from "./localization";
import { useCallback, useEffect, useRef, useState } from "react";
import { translations, getLocalizedError, type Language } from "../i18n";
import type { BrowserScreenshot, ChatMessage, Conversation, GeneratedFile, GeneratedImage, ScriptExecution, WebResearchState, WebSource } from "../types";
import { consumeChatStream } from "./stream";
import { createStreamBatcher } from "./stream-batcher";
import { isRecoverableStreamError, waitForStreamRecovery } from "./stream-recovery";
import { waitForImageJob } from "./image-jobs";
import { getClientId } from "./client-id";
import { getImageReferencesForRequest, stripInternalImageContext } from "./chat-context";
import { notifyGenerationComplete } from "./pwa";
import { ChatRequestError, fetchWithDeadline, readResponseJson } from "./request-errors";

const now = () => new Date().toISOString();
interface Options {
  language: Language;
  updateMessage: (conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => void;
  prepareConversationContext: (conversation: Conversation, messages: ChatMessage[]) => { messages: unknown[]; historyParentId?: string };
}
export function useChatGeneration({ language, updateMessage: commitMessage, prepareConversationContext }: Options) {
  const t = translations[language];
  const updateMessage = commitMessage;
  const detachedControllers = useRef(new WeakSet<AbortController>());
  const tasks = useRef(new Map<string, { sessionId: string; controller: AbortController; ready: Promise<void> }>());
  const [runningConversationIds, setRunningConversationIds] = useState<string[]>([]);
  const refreshTasks = () => setRunningConversationIds([...tasks.current.keys()]);
  useEffect(() => {
    const currentTasks = tasks.current;
    return () => { for (const task of currentTasks.values()) { detachedControllers.current.add(task.controller); task.controller.abort(); } currentTasks.clear(); };
  }, []);
  const generateResponse = useCallback(
    async (
      conversation: Conversation,
      contextMessages: ChatMessage[],
      assistantId: string,
      resumedMessage?: ChatMessage,
    ) => {
      const controller = new AbortController();
      const updateMessage = (conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => {
        if (!detachedControllers.current.has(controller)) commitMessage(conversationId, messageId, update);
      };
      if (tasks.current.has(conversation.id)) return;
      let acknowledgeStart!: () => void;
      const ready = new Promise<void>(resolve => { acknowledgeStart = resolve; });
      if (resumedMessage) acknowledgeStart();
      tasks.current.set(conversation.id, { controller, sessionId: resumedMessage?.generationSessionId ?? assistantId, ready });
      refreshTasks();
      const sessionId = resumedMessage?.generationSessionId ?? assistantId;
      const startedAt = Date.parse(resumedMessage?.createdAt ?? now());
      let content = resumedMessage?.content ?? "";
      let reasoning = resumedMessage?.reasoning ?? "";
      let generatedImages: GeneratedImage[] = resumedMessage?.generatedImages ?? [];
      let browserScreenshots: BrowserScreenshot[] = resumedMessage?.browserScreenshots ?? [];
      let generatedFiles: GeneratedFile[] = resumedMessage?.generatedFiles ?? [];
      let createdTasks: CreatedTask[] = resumedMessage?.createdTasks ?? [];
      let scriptExecutions: ScriptExecution[] = resumedMessage?.scriptExecutions ?? [];
      let webResearch: WebResearchState | undefined = resumedMessage?.webResearch;
      let webSources: WebSource[] = resumedMessage?.webSources ?? [];
      let pendingImageJobId = resumedMessage?.imageGeneration?.status === "generating"
        ? resumedMessage.imageGeneration.jobId
        : undefined;
      let imageGeneration = resumedMessage?.imageGeneration;
      let streamCursor = resumedMessage?.streamCursor ?? 0;
      let cancelledByServer = false;
      const streamBatcher = createStreamBatcher();

      if (resumedMessage) updateMessage(conversation.id, assistantId, message => ({ ...message, status: "streaming", error: undefined, errorCode: undefined, retryAt: undefined, elapsedMs: undefined, recoveryState: "recovering" }));

      const recoverPendingImage = async (): Promise<boolean> => {
        if (!pendingImageJobId) return false;
        const image = await waitForImageJob({
          jobId: pendingImageJobId,
          clientId: getClientId(),
          signal: controller.signal,
          language,
        });
        pendingImageJobId = undefined;
        imageGeneration = { status: "complete", modelId: image.modelId, modelName: image.modelName, prompt: image.prompt };
        if (!generatedImages.some((item) => item.id === image.id)) generatedImages = [...generatedImages, image];
        updateMessage(conversation.id, assistantId, (message) => ({
          ...message,
          generatedImages,
          imageGeneration: {
            status: "complete",
            modelId: image.modelId,
            modelName: image.modelName,
            prompt: image.prompt,
          },
          status: "streaming",
        }));
        void notifyGenerationComplete(language, "image");
        return true;
      };

      try {
      const contextPlan = prepareConversationContext(conversation, contextMessages);
      const requestBody = resumedMessage ? undefined : JSON.stringify({
        model: conversation.modelId,
        messages: contextPlan.messages,
        historyParentId: contextPlan.historyParentId,
        temperature: conversation.temperature,
        conversationId: conversation.id,
        reasoningEffort: supportedReasoningEffort(conversation.modelId, conversation.reasoningEffort),
        imageModel: conversation.imageModelId,
        imageReferences: getImageReferencesForRequest(contextMessages),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      });

        let recoveryAttempts = 0;
        let starting = !resumedMessage;
        while (true) {
          try {
            const endpoint = starting
              ? `/api/chat/sessions/${encodeURIComponent(sessionId)}`
              : `/api/chat/sessions/${encodeURIComponent(sessionId)}/events?cursor=${streamCursor}`;
            const response = await fetchWithDeadline(endpoint, {
              method: starting ? "POST" : "GET",
              headers: {
                ...(starting ? { "content-type": "application/json" } : {}),
                "x-neurondeck-client": getClientId(),
                "accept-language": localeFor(language),
              },
              ...(starting ? { body: requestBody } : {}),
              signal: controller.signal,
            });
            acknowledgeStart();

            if (!response.ok) {
              const data = (await readResponseJson(response, controller.signal)) as
                | { error?: { code?: string; message?: string; retryAfter?: number } }
                | null;
              const code = data?.error?.code ?? ([408, 429, 500, 502, 503, 504, 520, 522, 524].includes(response.status) ? "network_error" : undefined);
              throw new ChatRequestError(
                getLocalizedError(language, code, data?.error?.message || t.requestFailed(response.status)),
                code, data?.error?.retryAfter ?? (Number(response.headers.get("retry-after")) || undefined),
              );
            }
            starting = false;
            updateMessage(conversation.id, assistantId, (message) => ({
              ...message,
              generationSessionId: sessionId,
              streamCursor,
              recoveryState: recoveryAttempts ? "recovering" : undefined,
            }));

            await consumeChatStream(response, (event) => {
              // Replayed numbered events must never append duplicate text or tool output.
              if (event.cursor != null && event.cursor <= streamCursor) return;
              if (event.responseReset) { content = content.slice(0, event.responseReset.contentLength); reasoning = reasoning.slice(0, event.responseReset.reasoningLength); }
              if (event.serverConversationId === conversation.id) updateMessage(conversation.id, assistantId, (message) => ({ ...message, serverConversationId: conversation.id }));
              if (event.modelProgress) updateMessage(conversation.id, assistantId, (message) => ({ ...message, modelProgress: event.modelProgress }));
              if (event.createdTask || event.scriptExecution || event.imageGeneration || event.webResearch) updateMessage(conversation.id, assistantId, (message) => ({ ...message, modelProgress: undefined }));
              if (event.contextPhase) updateMessage(conversation.id, assistantId, (message) => ({ ...message, contextPhase: event.contextPhase }));
              if (event.cursor != null) streamCursor = Math.max(streamCursor, event.cursor);
              if (!event.error) recoveryAttempts = 0;
              if (event.content) content = stripInternalImageContext(content + event.content);
              if (event.reasoning) reasoning += event.reasoning;
              if (event.error) {
                throw new ChatRequestError(getLocalizedError(language, event.errorCode, event.error), event.errorCode, event.retryAfter);
              }
              if (event.cancelled) cancelledByServer = true;
              if (event.imageGeneration?.status === "generating" && event.imageGeneration.jobId) {
                pendingImageJobId = event.imageGeneration.jobId;
              }
              if (event.imageGeneration?.status === "error") pendingImageJobId = undefined;
              if (event.imageGeneration) imageGeneration = event.imageGeneration;
              if (event.generatedImage) {
                pendingImageJobId = undefined;
                imageGeneration = { status: "complete", modelId: event.generatedImage.modelId, modelName: event.generatedImage.modelName, prompt: event.generatedImage.prompt };
                if (!generatedImages.some((image) => image.id === event.generatedImage!.id)) {
                  generatedImages = [...generatedImages, event.generatedImage];
                }
              }
              if (event.scriptExecution) {
                const next = event.scriptExecution;
                scriptExecutions = scriptExecutions.some((item) => item.id === next.id)
                  ? scriptExecutions.map((item) => item.id === next.id ? next : item)
                  : [...scriptExecutions, next];
              }
              if (event.webResearch) {
                webResearch = event.webResearch;
                const source = event.webResearch.source;
                if (source && !webSources.some((item) => item.url === source.url)) webSources = [...webSources, source];
              }
              if (event.browserScreenshot && !browserScreenshots.some((item) => item.id === event.browserScreenshot!.id)) {
                browserScreenshots = [...browserScreenshots, event.browserScreenshot];
              }
              if (event.createdTask) {
                const next = event.createdTask;
                createdTasks = createdTasks.some(task => task.id === next.id) ? createdTasks.map(task => task.id === next.id ? next : task) : [...createdTasks, next];
              }
              if (event.generatedFile && !generatedFiles.some((item) => item.id === event.generatedFile!.id)) {
                generatedFiles = [...generatedFiles, event.generatedFile];
              }
              if (event.createdTask || event.scriptExecution || event.responseReset || event.content || event.reasoning || event.generatedImage || event.imageGeneration || event.webResearch || event.browserScreenshot || event.generatedFile || event.cursor != null) {
                streamBatcher.schedule(() => updateMessage(conversation.id, assistantId, (message) => ({
                  ...message,
                  error: undefined, errorCode: undefined, retryAt: undefined,
                  content,
                  reasoning,
                  generatedImages,
                  browserScreenshots,
                  generatedFiles,
                  createdTasks,
                  scriptExecutions,
                  webResearch,
                  webSources,
                  imageGeneration,
                  status: "streaming",
                  generationSessionId: sessionId,
                  streamCursor,
                  recoveryState: undefined,
                })));
              }
            }, { signal: controller.signal, network: window });
            streamBatcher.flush();
            break;
          } catch (error) {
            streamBatcher.flush();
            if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
            if (!isRecoverableStreamError(error)) throw error;

            recoveryAttempts += 1;
            const retryAfter = error instanceof ChatRequestError ? error.retryAfter ?? 0 : 0;
            await waitForStreamRecovery(controller.signal, Math.min(15_000, 300 * 2 ** Math.min(recoveryAttempts - 1, 6)), recoveryState => {
              updateMessage(conversation.id, assistantId, message => ({ ...message, status: "streaming", generationSessionId: sessionId, streamCursor, recoveryState, error: undefined, errorCode: undefined, retryAt: undefined, elapsedMs: undefined, contextPhase: undefined, modelProgress: undefined }));
            }, Date.now() + retryAfter * 1000);
          }
        }

        if (detachedControllers.current.has(controller)) return;
        if (webResearch && (webResearch.status === "searching" || webResearch.status === "reading" || webResearch.status === "capturing")) {
          webResearch = { ...webResearch, status: "complete" };
        }

        if (!cancelledByServer && !content.trim() && !generatedImages.length && !browserScreenshots.length && !generatedFiles.length && !createdTasks.length) {
          throw new ChatRequestError(getLocalizedError(language, "reasoning_incomplete", t.emptyCompletion), "reasoning_incomplete");
        }

        updateMessage(conversation.id, assistantId, (message) => ({
          ...message,
          content: cancelledByServer
            ? content || t.generationStopped
            : content || (generatedImages.length
              ? t.imageGeneratedFallback
              : browserScreenshots.length
                ? t.screenshotCapturedFallback
                : generatedFiles.length
                  ? t.fileGeneratedFallback
                  : createdTasks.length ? ui(language, "Task created. Open it to view or change the schedule.") : t.emptyCompletion),
          reasoning,
          generatedImages,
          browserScreenshots,
          generatedFiles,
          createdTasks,
          scriptExecutions: scriptExecutions.map((item) => item.status === "running" ? { ...item, status: "error" } : item),
          webResearch,
          webSources,
          imageGeneration: imageGeneration?.status === "generating" && cancelledByServer ? { ...imageGeneration, status: "error", message: t.generationStopped } : imageGeneration,
          status: "complete",
          modelProgress: undefined,
          contextPhase: undefined,
          elapsedMs: Math.max(1, Date.now() - startedAt),
          generationSessionId: sessionId,
          streamCursor,
          recoveryState: undefined,
        }));
        if (!cancelledByServer) {
          void notifyGenerationComplete(language, generatedImages.length ? "image" : "chat");
        }
      } catch (error) {
        if (detachedControllers.current.has(controller)) return;
        streamBatcher.flush();
        const stopped = error instanceof DOMException && error.name === "AbortError";
        if (!stopped && pendingImageJobId) {
          try {
            await recoverPendingImage();
          } catch {
            // Preserve the stream error below if the separate image job is also unavailable.
          }
        }
        const message = stopped
          ? content || t.generationStopped
          : error instanceof Error
            ? error.message
            : t.generationFailed;
        updateMessage(conversation.id, assistantId, (current) => ({
          ...current,
          content: content || (stopped ? t.generationStopped : ""),
          error: stopped ? undefined : message,
          errorCode: stopped ? undefined : error instanceof ChatRequestError ? error.code : isRecoverableStreamError(error) ? "network_error" : undefined,
          retryAt: error instanceof ChatRequestError && error.retryAfter ? new Date(Date.now() + error.retryAfter * 1000).toISOString() : undefined,
          reasoning,
          generatedImages,
          browserScreenshots,
          generatedFiles,
          createdTasks,
          scriptExecutions: scriptExecutions.map((item) => item.status === "running" ? { ...item, status: "error" } : item),
          webSources,
          imageGeneration: imageGeneration?.status === "generating" ? { ...imageGeneration, status: "error", message: stopped ? t.generationStopped : message } : imageGeneration,
          webResearch: webResearch && webResearch.status !== "complete" ? { ...webResearch, status: "error", message: stopped ? t.generationStopped : message } : webResearch,
          status: stopped ? "complete" : "error",
          modelProgress: undefined,
          contextPhase: undefined,
          elapsedMs: Math.max(1, Date.now() - startedAt),
          generationSessionId: sessionId,
          streamCursor,
          recoveryState: undefined,
        }));
      } finally {
        acknowledgeStart();
        streamBatcher.flush();
        if (tasks.current.get(conversation.id)?.controller === controller) tasks.current.delete(conversation.id);
        refreshTasks();
      }
    },
    [
      language,
      t.emptyCompletion,
      t.generationFailed,
      t.generationStopped,
      t.imageGeneratedFallback,
      t.fileGeneratedFallback,
      t.requestFailed,
      t.screenshotCapturedFallback,
      updateMessage,
    ],
  );

  const stopGeneration = useCallback(async (conversation: Conversation) => {
    const task = tasks.current.get(conversation.id);
    const pending = [...conversation.messages].reverse().find((message) => (message.status === "streaming" || message.errorCode === "image_cancel_pending" || message.errorCode === "cancel_failed") && (message.generationSessionId || message.id === task?.sessionId));
    const sessionId = task?.sessionId ?? pending?.generationSessionId;
    if (!sessionId) return;
    // Only stop this conversation. Wait for server acknowledgment before detaching the reader.
    try {
      const sendStop = () => fetchWithDeadline(`/api/chat/sessions/${encodeURIComponent(sessionId)}/cancel`, {
        method: "POST", headers: { "x-neurondeck-client": getClientId(), "content-type": "application/json" },
        body: JSON.stringify({ conversationId: conversation.id }), keepalive: true,
      });
      let response = await sendStop();
      if (response.status === 404 && task) {
        // A fast Stop can arrive before the initial POST creates its durable session.
        // Retain the intent until that exact request is admitted, then stop it again.
        void response.body?.cancel().catch(() => undefined);
        await task.ready;
        if (tasks.current.get(conversation.id) !== task || task.controller.signal.aborted) return;
        response = await sendStop();
      }
      if (!response.ok) {
        const data = await readResponseJson(response) as { error?: { code?: string; message?: string; retryAfter?: number } } | null;
        throw new ChatRequestError(getLocalizedError(language, data?.error?.code, data?.error?.message ?? t.requestFailed(response.status)), data?.error?.code ?? "cancel_failed", data?.error?.retryAfter);
      }
      task?.controller.abort();
      if (pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message, error: undefined, errorCode: undefined, retryAt: undefined }));
      if (!task && pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message, status: "complete", recoveryState: undefined }));
    } catch (error) {
      if (pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message,
        error: error instanceof ChatRequestError ? error.message : ui(language, "Stop was not confirmed. Please try again."),
        errorCode: error instanceof ChatRequestError ? error.code : "cancel_failed",
        retryAt: error instanceof ChatRequestError && error.retryAfter ? new Date(Date.now() + error.retryAfter * 1000).toISOString() : undefined,
      }));
    }
  }, [language, updateMessage, t.requestFailed]);
  const hasTask = useCallback((conversationId: string) => tasks.current.has(conversationId), []);
  return { generateResponse, stopGeneration, runningConversationIds, hasTask };
}
